import {
  ChangeEvent,
  ChangeEventHandler,
  forwardRef,
  ReactNode,
  Ref,
  useState,
} from 'react';
import pluralize from 'pluralize-ru';
// eslint-disable-next-line camelcase -- название из reakit
import { unstable_useId } from 'reakit';
import { Stack } from '../../Stack/Stack';
import { Label } from '../../Label/Label';
import { Hint } from '../../Hint/Hint';
import { Error } from '../../Error/Error';
import pxToRem from '../../utils/pxToRem';
import { Field, FieldProps } from '../../Field/Field';

function pluralizeCount(count: number): string {
  return pluralize(
    Math.abs(count),
    '%d символов',
    '%d символ',
    '%d символа',
    '%d символов'
  );
}

type CountTextProps = {
  /**
   * Количество введённых символов
   */
  current: number;
  /**
   * Максимально допустимое количество символов
   */
  max: number;
};

function CountText({ current, max }: CountTextProps) {
  let text = '';
  if (current !== 0 && current <= max)
    text = `Осталось ${pluralizeCount(max - current)} из ${max}`;
  if (current > max)
    text = `Вы ввели на ${pluralizeCount(current - max)} больше`;
  return (
    <Hint
      aria-live="polite"
      role="status"
      styles={{
        ':empty': { height: 0, mt: 0 },
      }}
    >
      {current >= max * 0.75 && text}
    </Hint>
  );
}

type TextAreaProps = FieldProps & {
  error?: string | ReactNode;
  symbolLimit: number;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

export const TextArea = forwardRef(function TextArea(
  { error, symbolLimit, ...props }: TextAreaProps,
  ref: Ref<HTMLTextAreaElement>
) {
  let [count, setCount] = useState<number>(0);
  let { id } = unstable_useId();
  return (
    <Stack space="sp-8">
      <Stack space="sp-4" as="label" htmlFor={id}>
        <Label>Добавьте комментарий по желанию</Label>
        {error && <Error key="error">{error}</Error>}
      </Stack>
      <Field
        {...props}
        ref={ref}
        name="comment"
        as="textarea"
        value={props.value}
        id={id}
        styles={{
          minHeight: pxToRem(128),
        }}
        hasError={Boolean(error)}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          props.onChange(event);
          setCount(event.target.value.length);
        }}
      />
      <CountText
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing  -- здесь нужна такая проверка
        current={(props.value as string | undefined)?.length || count}
        max={symbolLimit || 400}
      />
    </Stack>
  );
});
